import {
  base_colorAction,
  base_colorAlert,
  base_colorContentBorder,
  base_colorSurfaceSecondary,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export default css`
  display: flex;
  flex-direction: column;

  /* Mirrors the design-render KV primitive: a hairline-divided row per figure,
     baseline-aligned, secondary-grey label left, tabular-nums value right.
     EVERY row carries its own bottom border — the tile previously drew a single
     rule after the whole group, so Card limit and Company line ran together. */
  .SpendLimitTile-kv {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: ${spacing(4)};
    padding: ${spacing(3)} 0;
    border-bottom: 1px solid ${base_colorContentBorder};
  }

  .SpendLimitTile-kvLabel {
    display: inline-flex;
    align-items: center;
    gap: ${spacing(2)};
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .SpendLimitTile-kvValue {
    font-family: ${font_family_weight_regular};
    ${font_md};
    color: ${text_colorTextPrimary};
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  /* Focusable info trigger — a real button, so the tooltip is reachable by
     keyboard. Stripped back to look like the design's inline glyph, with the
     focus ring kept (never remove it without a replacement). */
  .SpendLimitTile-kvInfo {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: ${text_colorTextSecondary};
  }

  .SpendLimitTile-kvInfo > svg g {
    fill: currentColor;
  }

  .SpendLimitTile-kvInfo:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
    border-radius: ${spacing(1)};
  }

  /* The design KV's "strong" variant — the pinned figure (the card limit
     itself), not every value. The company line is a supporting read-only
     readout and stays regular weight. */
  .SpendLimitTile-kvValue.strong {
    font-family: ${font_family_weight_medium};
  }

  /* Spacing the "Adjust spend limit" trigger away from the KV rows above it —
     previously this gap was the inline "Adjust the spend limit" heading's own
     top margin, which moved into the drawer with the form. */
  .SpendLimitTile-adjustTrigger {
    margin-top: ${spacing(3)};
    align-self: flex-start;
  }

  /* Matches .SettingsDisclosure-title (e.g. "Billing address") so section
     headings across the tiles read at the same size. Rubik faces are all
     registered at font-weight: normal, so the browser's default
     <h4> { font-weight: bold } would layer a synthetic bold on top of
     Rubik-Medium — pin it to normal and let the family carry the weight. */
  .SpendLimitTile-heading {
    margin: ${spacing(5)} 0 ${spacing(1)};
    font-family: ${font_family_weight_medium};
    font-weight: normal;
    ${font_md};
    letter-spacing: -0.005em;
    color: ${text_colorTextPrimary};
  }

  .SpendLimitTile-body {
    margin: 0 0 ${spacing(4)};
    max-width: 58ch;
    font-family: ${font_family_weight_regular};
    ${font_md};
    line-height: 21px;
    color: ${text_colorTextSecondary};
  }

  .SpendLimitTile-form {
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
    max-width: 460px;
  }

  .SpendLimitTile-form .SettingsSegmented {
    align-self: flex-start;
  }

  .SpendLimitTile-dates {
    display: flex;
    gap: ${spacing(3)};
  }

  .SpendLimitTile-dates > * {
    flex: 1;
  }

  .SpendLimitTile-confirmation {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing(2)};
    padding: ${spacing(4)};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${spacing(2)};
    background-color: ${base_colorSurfaceSecondary};
    max-width: 460px;
  }

  .SpendLimitTile-confirmationHeading {
    display: inline-flex;
    align-items: center;
    gap: ${spacing(2)};
    font-family: ${font_family_weight_medium};
    ${font_md};
    color: ${text_colorTextPrimary};
  }

  .SpendLimitTile-confirmationBody {
    margin: 0;
    font-family: ${font_family_weight_regular};
    ${font_md};
    color: ${text_colorTextSecondary};
  }

  .SpendLimitTile-formActions {
    display: flex;
    align-items: center;
    gap: ${spacing(3)};
    flex-wrap: wrap;
  }

  .SpendLimitTile-savedNote {
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .SpendLimitTile-errorNote {
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${base_colorAlert};
  }
`
